import React, { useState } from "react";
import { Loader2, Plus, Users } from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useAccessManagement } from "./hooks/useAccessManagement";
import SubAccountForm from "./SubAccountForm";
import SubAccountList from "./SubAccountList";
import RemoveSubAccountModal from "./RemoveSubAccountModal";
import { ISubAccount } from "./types";

export default function AccessManagement() {
  const {
    loading,
    submitting,
    accounts,
    properties,
    showForm,
    editingAccount,
    feedback,
    errorMessage,
    openCreateForm,
    openEditForm,
    closeForm,
    handleSubmit,
    handleToggleActive,
    handleDelete,
  } = useAccessManagement();
  const [accountToRemove, setAccountToRemove] = useState<ISubAccount | null>(
    null
  );

  async function confirmRemoval() {
    if (!accountToRemove) {
      return;
    }

    const removed = await handleDelete(accountToRemove);
    if (removed) {
      setAccountToRemove(null);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header hasBorder hasSearchBar hasOptionToSearch />

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-[#DD6435]" />
              <h1 className="text-2xl font-bold text-[#2D3748]">Gestão de Acessos</h1>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-[#64748B]">
              Crie e-mails alternativos com senha própria e acesso apenas aos
              imóveis que você selecionar. Ideal para a equipe operar o painel
              sem usar a conta principal.
            </p>
          </div>

          {!showForm && (
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#DD6435] px-5 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Novo acesso
            </button>
          )}
        </div>

        {feedback && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {feedback}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center bg-transparent">
            <Loader2
              className="h-10 w-10 animate-spin text-[#DD6435]"
              aria-label="Carregando"
            />
          </div>
        ) : (
          <div className="space-y-6">
            {showForm && (
              <SubAccountForm
                mode={editingAccount ? "edit" : "create"}
                initialAccount={editingAccount}
                properties={properties}
                isSubmitting={submitting}
                onSubmit={handleSubmit}
                onCancel={closeForm}
              />
            )}

            <SubAccountList
              accounts={accounts}
              properties={properties}
              isUpdating={submitting}
              onEdit={openEditForm}
              onToggleActive={handleToggleActive}
              onDelete={setAccountToRemove}
            />
          </div>
        )}
      </main>

      <Footer />

      {accountToRemove && (
        <RemoveSubAccountModal
          account={accountToRemove}
          isDeleting={submitting}
          onCancel={() => setAccountToRemove(null)}
          onConfirm={confirmRemoval}
        />
      )}
    </div>
  );
}
